import { useState, FormEvent } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import {
  Mail,
  Lock,
  ArrowRight,
  Loader2,
  Terminal,
  Shield,
  Zap,
  Clock,
} from "lucide-react";
import { motion } from "framer-motion";
import logo from "../assets/logo.svg";
import ThemeToggle from "../components/ThemeToggle";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from =
    (location.state as { from?: { pathname: string } })?.from?.pathname ||
    "/dashboard";

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      toast.info("Authenticating...", {
        description: "Validating credentials",
      });

      const result = await login(email, password);

      if (result.success) {
        toast.success("Access Granted", {
          description: "JWT token issued - redirecting to dashboard",
        });
        navigate(from, { replace: true });
      } else {
        toast.error("Access Denied", {
          description:
            result.error || "Authentication failed. Invalid email or password.",
          duration: 5000,
        });
      }
    } catch (error) {
      toast.error("System Error", {
        description: "Unable to process authentication request",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    {
      icon: Terminal,
      title: "Real-Time Processing",
      metric: "< 100ms",
    },
    {
      icon: Shield,
      title: "Enterprise Security",
      metric: "SOC 2",
    },
    {
      icon: Zap,
      title: "High Availability",
      metric: "99.99%",
    },
    {
      icon: Clock,
      title: "Avg. Resolution",
      metric: "2.4h",
    },
  ];

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#16181d_1px,transparent_1px),linear-gradient(to_bottom,#16181d_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_110%)] dark:opacity-100 opacity-[0.03]\" />

      <div className="absolute top-4 right-4 z-50">
        <ThemeToggle />
      </div>

      <div className="relative z-10 min-h-screen flex flex-col lg:flex-row">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="hidden lg:flex lg:w-1/2 xl:w-2/5 flex-col justify-between p-8 md:p-12 xl:p-16"
        >
          <div>
            <div className="inline-flex items-center gap-3 mb-12">
              <div className="relative">
                <div className="absolute inset-0 bg-primary/20 blur-xl animate-pulse" />
                <div className="relative w-16 h-16 flex items-center justify-center">
                  <img src={logo} alt="CamPulse logo" className="w-16 h-16" />
                </div>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground tracking-tight">
                  <span className="text-primary font-semibold">Cam</span>
                  <span className="font-extralight">Pulse</span>
                </h1>
                <p className="text-xs text-muted-foreground tracking-wider uppercase">
                  Service Management
                </p>
              </div>
            </div>

            <div className="space-y-6 max-w-md">
              <h2 className="text-4xl xl:text-5xl font-bold text-foreground leading-tight tracking-tight">
                The Heartbeat of Campus IT
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Incident management, service requests, and automated workflows
                designed for modern educational institutions.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 md:gap-4 max-w-md">
            {features.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * idx, duration: 0.5 }}
                  className="relative group"
                >
                  <div className="absolute inset-0 bg-primary/5 rounded-lg blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative bg-card border border-border/50 rounded-lg p-4 hover:border-primary/30 transition-all">
                    <Icon className="w-5 h-5 text-primary mb-2" />
                    <div className="text-2xl font-bold text-foreground mb-1 font-mono">
                      {feature.metric}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {feature.title}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex-1 flex items-center justify-center p-4 sm:p-6 md:p-8"
        >
          <div className="w-full max-w-md">
            <div className="lg:hidden flex items-center gap-3 mb-8">
              <div className="relative">
                <div className="absolute inset-0 bg-primary/20 blur-xl animate-pulse" />
                <div className="relative w-10 h-10 flex items-center justify-center">
                  <img src={logo} alt="CamPulse logo" />
                </div>
              </div>
              <div>
                <h1 className="text-xl font-bold text-foreground tracking-tight">
                  <span className="text-primary font-semibold">Cam</span>
                  <span className="font-extralight">Pulse</span>
                </h1>
                <p className="text-xs text-muted-foreground tracking-wider uppercase">
                  Service Management
                </p>
              </div>
            </div>
            <div className="bg-card border border-border/50 rounded-2xl p-6 sm:p-8 shadow-2xl">
              <div className="mb-6 sm:mb-8">
                <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-2 font-serif">
                  Authentication
                </h3>
                <p className="text-sm text-muted-foreground">
                  Enter your credentials to access the system
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <label
                    htmlFor="email"
                    className="text-xs font-medium text-foreground uppercase tracking-wider"
                  >
                    Email Address
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-0 bg-primary/10 rounded-lg blur-sm opacity-0 group-focus-within:opacity-100 transition-opacity" />
                    <div className="relative flex items-center">
                      <Mail className="absolute left-4 w-4 h-4 text-muted-foreground" />
                      <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full pl-11 pr-4 py-3.5 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground text-sm"
                        placeholder="user@campus.edu"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="password"
                    className="text-xs font-medium text-foreground uppercase tracking-wider"
                  >
                    Password
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-0 bg-primary/10 rounded-lg blur-sm opacity-0 group-focus-within:opacity-100 transition-opacity" />
                    <div className="relative flex items-center">
                      <Lock className="absolute left-4 w-4 h-4 text-muted-foreground" />
                      <input
                        id="password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full pl-11 pr-4 py-3.5 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground text-sm"
                        placeholder="Enter password"
                        required
                      />
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="relative w-full group mt-6 cursor-pointer"
                >
                  <div className="absolute inset-0 bg-primary/20 rounded-lg blur-lg group-hover:blur-xl transition-all" />
                  <div className="relative bg-primary hover:bg-primary/90 text-white font-semibold py-3.5 px-4 rounded-lg transition-all flex items-center justify-center gap-2 shadow-lg">
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Authenticating...</span>
                      </>
                    ) : (
                      <>
                        <span>Sign In</span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </div>
                </button>
              </form>
            </div>

            <p className="mt-8 text-center text-xs text-muted-foreground">
              © 2026 <span className="text-primary font-semibold">Cam</span>
              <span className="font-extralight">Pulse</span>. The heartbeat of
              campus IT operations.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
